/**
 * ===========================================================
 * 🧩 FeatureManager + ConfigManager Unified Helper
 * -----------------------------------------------------------
 * Centraliza el manejo de feature flags, toggles del customizer
 * y experimentos en Shopify + GrowthBook.
 *
 * Incluye:
 *  - Soporte para holdout global
 *  - Evaluación de features
 *  - Callbacks reactivos con setRenderer()
 *  - Integración con toggles del customizer (config manual)
 *  - Helper para togglear clases en el DOM
 * ===========================================================
 */

window.FeatureManager = (() => {
  const growthbook = window._growthbook;
  const HOLDOUT_KEY = "anolon-holdout";
  const featureCallbacks = new Map();

  // Estado local de overrides (Customizer o API)
  let localConfig = {};

  /* ---------------------------------------------------------
   * 📦 CONFIG MANAGEMENT (Customizer / Local overrides)
   * --------------------------------------------------------- */

  /**
   * Inicializa o extiende la configuración local.
   * Ejemplo:
   * FeatureManager.initConfig({ simplified_menu: true });
   */
  function initConfig(config = {}) {
    localConfig = { ...localConfig, ...config };
  }

  /**
   * Obtiene todo el estado actual de configuración local.
   */
  function getConfig() {
    return { ...localConfig };
  }

  /**
   * Obtiene el override local (si existe)
   */
  function getLocalValue(featureKey) {
    return localConfig.hasOwnProperty(featureKey)
      ? localConfig[featureKey]
      : false;
  }

  /* ---------------------------------------------------------
   * 🚦 FEATURE EVALUATION (GrowthBook + Holdout + Customizer)
   * --------------------------------------------------------- */

  function isEnabled(featureKey) {
    if (!growthbook) {
      console.warn("⚠️ GrowthBook no inicializado aún.");
      return false;
    }

    // Holdout global → nunca activar
    if (growthbook.isOn(HOLDOUT_KEY)) return false;

    return growthbook.isOn(featureKey);
  }
  /**
* 🔀 Combina el Customizer con GrowthBook
* - Si el toggle está en true → fuerza el estado
* - Si está en false o no existe → GrowthBook decide
*/
  function getEffectiveFeatureState({ featureKey, defaultValue = false }) {
    const localValue = getLocalValue(featureKey);

    // 1️⃣ Si el merchant lo activó manualmente → prioridad
    if (localValue === true) {
      return true;
    }

    // 2️⃣ Si no existe o está en false → GrowthBook decide
    if (isEnabled(featureKey)) {
      return true;
    }

    // 3️⃣ Ninguno lo activa → fallback
    return defaultValue;
  }

  /* ---------------------------------------------------------
   * 🔁 CALLBACKS / REACTIVITY
   * --------------------------------------------------------- */

  function onFeatureChange(featureKey, callback, runImmediately = true) {
    if (!featureCallbacks.has(featureKey)) {
      featureCallbacks.set(featureKey, []);
    }

    featureCallbacks.get(featureKey).push(callback);

    if (runImmediately) {
      callback(getEffectiveFeatureState({ featureKey }));
    }
  }

  /**
   * 🎨 Atajo para togglear clases según el estado del feature
   */
  function toggleClass(featureKey, selector, className) {
    onFeatureChange(featureKey, (enabled) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.toggle(className, enabled);
      });
    });
  }

  /**
   * ⚙️ Inicializa el renderer global de GrowthBook
   * para disparar los callbacks cada vez que cambia un experimento.
   */
  function initRenderer() {
    if (!growthbook?.setRenderer) return;

    growthbook.setRenderer(() => {
      featureCallbacks.forEach((callbacks, featureKey) => {
        const enabled = getEffectiveFeatureState({ featureKey });
        callbacks.forEach((cb) => cb(enabled));
      });
    });
  }

  // Inicializa el renderer global al cargar
  initRenderer();

  /* ---------------------------------------------------------
   * 📡 API Pública
   * --------------------------------------------------------- */
  return {
    // Config
    initConfig,
    getConfig,
    getLocalValue,

    // Feature evaluation
    isEnabled,
    getEffectiveFeatureState,

    // Reactivity
    onFeatureChange,
    toggleClass,
  };
})();

/**
 * Helpers para identificar tipo de página en Shopify
 * Se basan en el body tag y la variable window.Shopify
 */

function isHomePage() {
  return document.body.classList.contains('template-index');
}

function isProductPage() {
  return document.body.classList.contains('template-product');
}

function isCollectionPage() {
  return document.body.classList.contains('template-collection');
}

function isSearchPage() {
  return document.body.classList.contains('template-search');
}